import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/Link";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useEffect, type FC, useState, useRef, useCallback, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { CopyableTextArea } from "@/components/podkit/forms/CopyableTextArea";
import { useCreateRunnerExchangeToken } from "@/queries/runner-queries";
import { Check, InfoIcon } from "lucide-react";
import { Button } from "@/components/flexkit/Button";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { toast } from "@/components/podkit/toasts/use-toast";
import { LinuxRunnerDetails } from "@/components/runners/details/LinuxRunnerDetails";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";

export const LinuxRunnerSetup: FC<{ runner: PlainMessage<Runner>; hasSignedUp: boolean }> = ({
    runner,
    hasSignedUp,
}) => {
    return (
        <div className="flex flex-col gap-4">
            <Text className="text-base font-semibold">Linux Runner Setup</Text>
            {hasSignedUp ? <LinuxRunnerDetails runner={runner} /> : <LinuxRunnerCreate runner={runner} />}
        </div>
    );
};

export const LinuxRunnerCreate: FC<{ runner: PlainMessage<Runner> }> = ({ runner }) => {
    const createRunnerExchangeToken = useCreateRunnerExchangeToken();
    const [exchangeToken, setExchangeToken] = useState<string>();
    const [generated, setGenerated] = useTemporaryState(false, 2000);
    const calledOnce = useRef(false);

    const generateToken = useCallback(
        async (fromButton: boolean) => {
            try {
                const result = await createRunnerExchangeToken.mutateAsync(runner.runnerId);
                setExchangeToken(result.exchangeToken);
                if (fromButton) {
                    setGenerated(true);
                }
            } catch (error) {
                console.error("Failed to generate exchange token", error);
                toast({
                    title: "Failed to generate token",
                    description: "Please try again later",
                });
            }
        },
        [createRunnerExchangeToken, runner.runnerId, setGenerated],
    );

    useEffect(() => {
        calledOnce.current = false;
    }, [runner.runnerId]);

    useEffect(() => {
        if (calledOnce.current) {
            return;
        }
        calledOnce.current = true;

        void generateToken(false);
    }, [runner.runnerId, createRunnerExchangeToken, generateToken]);

    const apiEndpoint = useMemo(() => {
        const isGitpodIO = window.location.origin === "https://app.gitpod.io";
        if (isGitpodIO) {
            return null;
        }
        return `${window.location.origin}/api`;
    }, []);

    const setupCommand = useMemo(() => {
        if (!exchangeToken) return "Loading...";

        let command = `gitpod runner setup --exchange-token ${exchangeToken}`;
        if (apiEndpoint) {
            command += ` --host ${apiEndpoint}`;
        }
        return command;
    }, [exchangeToken, apiEndpoint]);

    return (
        <div className="flex flex-col gap-6 pb-4">
            <div className="rounded-lg bg-surface-secondary p-3">
                <Text className="text-sm text-content-secondary">
                    Run these commands on your Linux machine where you want to install the runner.
                </Text>
            </div>

            <div className="flex flex-col divide-y divide-border-base">
                {/* Step 1: Install CLI */}
                <SetupStep number={1} title="Install Gitpod CLI" description="Download and install the latest version of the Gitpod CLI">
                    <CopyableTextArea
                        className="font-mono text-sm"
                        copyable
                        value={`wget -O gitpod "https://releases.gitpod.io/cli/stable/gitpod-linux-amd64"
chmod +x gitpod
sudo mv gitpod /usr/local/bin`}
                    />
                </SetupStep>

                {/* Step 2: Configure Runner */}
                <SetupStep
                    number={2}
                    title="Run Setup Command"
                    description="The setup command will validate your system and guide you through required changes, including permissions setup."
                >
                    <div className="flex flex-col gap-3">
                        <CopyableInput
                            className={cn(
                                "w-full max-w-none pr-12 font-mono text-sm",
                                (!exchangeToken || createRunnerExchangeToken.isPending) && "animate-pulse opacity-50",
                            )}
                            copyable={!!exchangeToken && !createRunnerExchangeToken.isPending}
                            value={setupCommand}
                        />
                        <div className="flex items-center justify-between gap-2">
                            <Text className="text-xs text-content-tertiary">
                                Token expires in 24 hours, or after one-time use
                            </Text>
                            <Button
                                variant="ghost"
                                type="button"
                                size="sm"
                                className="flex items-center gap-2"
                                loading={createRunnerExchangeToken.isPending}
                                LeadingIcon={() => (generated ? <Check className="text-green-500" size={12} /> : null)}
                                onClick={() => generateToken(true)}
                            >
                                Generate new token
                            </Button>
                        </div>
                        <div className="rounded-lg bg-surface-secondary p-3 flex items-start gap-2">
                            <div className="text-amber-500 mt-0.5">
                                <InfoIcon size={16} />
                            </div>
                            <div>
                                <Text className="text-sm font-medium text-content-primary">
                                    Run this command repeatedly until all checks pass
                                </Text>
                                <Text className="text-sm text-content-secondary">
                                    Follow each instruction, then run the command again to verify and continue.
                                </Text>
                            </div>
                        </div>
                    </div>
                </SetupStep>

                {/* Step 3: Start Runner */}
                <SetupStep number={3} title="Start Runner" description="Launch your runner service">
                    <div className="flex flex-col gap-3">
                        <CopyableInput
                            copyable
                            className="w-full max-w-none pr-12 font-mono text-sm"
                            value="gitpod runner run"
                        />
                        <div className="flex items-center gap-2">
                            <ExternalLink
                                href="https://www.gitpod.io/docs/flex/runners/linux/setup#optional-systemd-service-setup"
                                iconSize="sm"
                                className="text-xs text-content-secondary"
                            >
                                Set up as systemd service to keep it running
                            </ExternalLink>
                        </div>
                    </div>
                </SetupStep>
            </div>

            <div className="flex items-center gap-3">
                <ExternalLink href="https://www.gitpod.io/docs/flex/runners/linux" iconSize="sm" className="text-sm">
                    View Documentation
                </ExternalLink>
                <Text className="text-sm text-content-secondary">•</Text>
                <ExternalLink
                    href="https://www.gitpod.io/docs/flex/runners/linux/troubleshooting"
                    iconSize="sm"
                    className="text-sm"
                >
                    Troubleshooting Guide
                </ExternalLink>
            </div>
        </div>
    );
};

const SetupStep: FC<{
    number: number;
    title: string;
    description: string;
    children: React.ReactNode;
}> = ({ number, title, description, children }) => (
    <div className="flex flex-col gap-2 py-6 first:pt-4 last:pb-4">
        <div className="flex items-center gap-3">
            <Text className="font-medium">
                {number}. {title}
            </Text>
        </div>
        <Text className="text-sm text-content-secondary">{description}</Text>
        <div>{children}</div>
    </div>
);
